<template>
    <div>
        <div class="modal-body edit-signer">
            <section class="edit-signer-card">
                <div class="edit-signer-header">
                    <div>
                        <div class="edit-signer-name">{{ signer.name || '-' }}</div>
                        <div class="text-muted">{{ signAsLabel }}</div>
                    </div>
                    <div class="edit-signer-tags">
                        <span v-if="isV3 && signer.signature_mode === 'presential'" class="badge badge-info">Assinatura presencial</span>
                        <span class="badge" :class="hasSigned ? 'badge-success' : 'badge-secondary'">
                            {{ hasSigned ? 'Assinado' : 'Pendente' }}
                        </span>
                    </div>
                </div>
                <div class="row gutters">
                    <div class="col-lg-3 col-md-6 col-12">
                        <div class="form-group mb-lg-0">
                            <label for="signer">{{ t('DOCUMENTS.SIGNER_DOC.SIGNER') }}</label>
                            <input type="text" class="form-control" id="signer" v-model="signer.name" disabled>
                        </div>
                    </div>
                    <div class="col-lg-3 col-md-6 col-12">
                        <div class="form-group mb-lg-0">
                            <label for="taxpayer">{{ t('DOCUMENTS.SIGNER_DOC.TAXPAYER') }}</label>
                            <input type="text" class="form-control" id="taxpayer" v-model="signer.documentation" disabled>
                        </div>
                    </div>
                    <div class="col-lg-3 col-md-6 col-12">
                        <div class="form-group mb-lg-0">
                            <label for="email">{{ t('DOCUMENTS.SIGNER_DOC.MAIL') }}</label>
                            <input type="text" class="form-control" id="email" v-model="signer.email" disabled>
                        </div>
                    </div>
                    <div class="col-lg-3 col-md-6 col-12">
                        <div class="form-group mb-0">
                            <label for="phone">{{ t('DOCUMENTS.SIGNER_DOC.PHONE') }}</label>
                            <input type="text" class="form-control" id="phone" v-model="signer.phone_number" disabled>
                        </div>
                    </div>
                </div>
            </section>

            <section v-if="supportsPresentialSignature" class="edit-signer-card">
                <h6 class="edit-signer-title"><i class="icon-users"/> Modo de assinatura</h6>
                <div class="row gutters">
                    <div class="col-md-6 col-12" v-for="option in signatureModeOptions" :key="option.id">
                        <label class="edit-signer-mode" :class="{'is-selected': (signer.signature_mode ?? 'remote') === option.id, 'is-disabled': hasSigned}">
                            <input type="radio" name="signature_mode" :value="option.id"
                                   v-model="signer.signature_mode" :disabled="hasSigned" @change="changeSignatureMode()">
                            <span>
                                <strong>{{ option.text }}</strong>
                                <small class="d-block text-muted">{{ option.description }}</small>
                            </span>
                        </label>
                    </div>
                </div>
                <div v-if="signer.signature_mode === 'presential'" class="row gutters mt-2">
                    <div class="col-md-6 col-12">
                        <div class="form-group">
                            <label for="signature_host_name" class="required">Nome do anfitrião</label>
                            <input type="text" class="form-control" id="signature_host_name"
                                   v-model="signer.signature_host.name" :disabled="hasSigned">
                        </div>
                    </div>
                    <div class="col-md-6 col-12">
                        <div class="form-group">
                            <label for="signature_host_email" class="required">E-mail do anfitrião</label>
                            <input type="email" class="form-control" id="signature_host_email"
                                   v-model="signer.signature_host.email" :disabled="hasSigned">
                        </div>
                    </div>
                    <div class="col-12">
                        <small class="text-muted">
                            O token de assinatura (6 caracteres, válido por 24 horas) é enviado por e-mail ao anfitrião
                            quando a sessão presencial é iniciada.
                        </small>
                    </div>
                </div>
            </section>

            <section class="edit-signer-card">
                <h6 class="edit-signer-title"><i class="icon-lock"/> {{ t('DOCUMENTS.AUTHS') }}</h6>
                <div class="row gutters">
                    <div class="col-md-6 col-12">
                        <div class="form-group mb-0">
                            <label>Canal de autenticação</label>
                            <Select2 v-model="signer.auths" placeholder="Selecione"
                                     :options="authOptionsForSigner"
                                     :disabled="hasSigned"
                                     @select="changeAuth()"
                                     :settings="{width:'100%'}"></Select2>
                        </div>
                    </div>
                    <div class="col-md-6 col-12 align-self-end">
                        <small v-if="signer.signature_mode === 'presential'" class="text-muted">
                            Na assinatura presencial o certificado digital (ICP-Brasil) não está disponível.
                        </small>
                        <small v-else-if="signer.auths === 'icp_brasil'" class="text-muted">
                            Com certificado digital as validações de identidade abaixo não se aplicam.
                        </small>
                    </div>
                </div>
            </section>

            <section class="edit-signer-card">
                <h6 class="edit-signer-title"><i class="icon-settings1"/> Validações de identidade</h6>
                <div class="row gutters">
                    <div class="col-lg-4 col-md-6 col-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" id="selfie_enabled"
                                   v-model="signer.selfie_enabled" :disabled="hasSigned || signer.auths === 'icp_brasil'">
                            <label class="custom-control-label" for="selfie_enabled">{{ t('DOCUMENTS.SELFIE_ENABLED') }}</label>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6 col-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" id="official_document_enabled"
                                   v-model="signer.official_document_enabled" :disabled="hasSigned || signer.auths === 'icp_brasil'">
                            <label class="custom-control-label" for="official_document_enabled">{{ t('DOCUMENTS.OFFICIAL_DOCUMENT_ENABLED') }}</label>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6 col-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" id="handwritten_enabled"
                                   v-model="signer.handwritten_enabled" :disabled="hasSigned || signer.auths === 'icp_brasil'">
                            <label class="custom-control-label" for="handwritten_enabled">{{ t('DOCUMENTS.HANDWIRITTEN_ENABLED') }}</label>
                        </div>
                    </div>
                </div>
            </section>
        </div>
        <div class="modal-footer flex-column align-items-stretch">
            <div class="alert alert-warning mb-2" v-if="hasSigned">
                Este signatário já assinou o documento e suas configurações não podem mais ser alteradas.
            </div>
            <div class="alert alert-info mb-2" v-else-if="isV3 && changesResendLink">
                Alterar o canal de autenticação ou o modo de assinatura gera um novo link de assinatura,
                e o signatário será notificado novamente.
            </div>
            <div class="d-flex justify-content-end">
                <button class="btn btn-outline-secondary mr-2" data-dismiss="modal">Cancelar</button>
                <button class="btn btn-primary" :disabled="hasSigned" @click="saveSigner">Salvar</button>
            </div>
        </div>
    </div>
</template>

<script>
import {useI18n} from 'vue-i18n';
import {useToast} from 'vue-toastification';
import {mapActions, mapState} from 'vuex';
import Select2 from 'vue3-select2-component';
import Helper from '@/Helpers/helper';
import {isV3Signer} from '../../../services/clicksignVersion';

export default {
    name: "editSignerComponent",
    setup() {
        const {t} = useI18n();
        const toast = useToast();
        return {t, toast}
    },
    components:{
        Select2
    },
    mounted() {
        if (localStorage.getItem('company_id') && ['4d45d80c-829c-4013-80a6-b0d036012b59','ca8ecad3-9e79-4bce-965c-1d7ed59361f0'].includes(localStorage.getItem('company_id'))){
            this.optionZap = [{id:'whatsapp', text: 'Whatsapp'}];
        }

        if (!this.supportsPresentialSignature) {
            this.signer.signature_mode = 'remote';
            this.signer.signature_host = null;
        }
    },
    watch: {
        'signer.id': {
            immediate: true,
            handler() {
                if (this.signer?.id && !this.signer.signature_mode) {
                    this.signer.signature_mode = 'remote';
                }
                this.originalConfig = this.signer?.id ? {
                    auths: this.signer.auths,
                    signature_mode: this.signer.signature_mode ?? 'remote',
                    signature_host: JSON.stringify(this.signer.signature_host ?? null),
                } : null;
            },
        },
    },
    computed: {
        ...mapState({signer: (state) => state.document.showSigner}),
        supportsPresentialSignature() {
            return isV3Signer(this.signer);
        },
        isV3() {
            return isV3Signer(this.signer);
        },
        signAsLabel() {
            return this.signer.sign_as ? Helper.translate(this.signer.sign_as) : '';
        },
        hasSigned() {
            return this.signer.status_of_signature === 'signed';
        },
        changesResendLink() {
            if (!this.originalConfig) {
                return false;
            }
            return this.originalConfig.auths !== this.signer.auths
                || this.originalConfig.signature_mode !== (this.signer.signature_mode ?? 'remote')
                || this.originalConfig.signature_host !== JSON.stringify(this.signer.signature_host ?? null);
        },
        authOptionsForSigner() {
            const baseOptions = [
                {id: 'email', text: 'E-mail'},
                {id: 'sms', text: 'SMS'},
                ...this.optionZap,
            ];

            if (this.signer.signature_mode === 'presential') {
                return baseOptions;
            }

            if (['seller', 'witness'].includes(this.signer.sign_as)) {
                return [...baseOptions, {id: 'icp_brasil', text: 'ICP-Brasil'}];
            }

            return baseOptions;
        }
    },
    data(){
        return{
            optionZap:[],
            originalConfig: null,
            signatureModeOptions:[
                {id: 'remote', text: 'Remota', description: 'O signatário recebe o link por e-mail, SMS ou WhatsApp e assina de onde estiver.'},
                {id: 'presential', text: 'Presencial', description: 'O signatário assina no local, com o token enviado ao anfitrião.'},
            ],
        }
    },
    methods:{
        ...mapActions({
            alterConfigSignerDoc:'alterConfigSignerDoc'
        }),
        changeAuth()
        {
            if (this.signer.auths === 'icp_brasil'){
                this.signer.selfie_enabled = false;
                this.signer.handwritten_enabled = false;
                this.signer.official_document_enabled = false;
            }
        },
        changeSignatureMode()
        {
            if (!this.supportsPresentialSignature) {
                this.signer.signature_mode = 'remote';
                this.signer.signature_host = null;
                return;
            }

            if (this.signer.signature_mode !== 'presential') {
                this.signer.signature_host = null;
                return;
            }

            this.signer.signature_host = this.signer.signature_host || {name: '', email: ''};

            if (this.signer.auths === 'icp_brasil') {
                this.signer.auths = 'email';
            }
        },
        saveSigner()
        {
            if(!this.signer.id){
                this.toast.error('Necessario selecionar um signátario');
                return;
            }

            if (this.hasSigned) {
                this.toast.error('Este signatário já assinou o documento e suas configurações não podem mais ser alteradas.');
                return;
            }

            if (!this.signer.signature_mode) {
                this.signer.signature_mode = 'remote';
            }

            if (!this.supportsPresentialSignature) {
                this.signer.signature_mode = 'remote';
                this.signer.signature_host = null;
            }

            if (this.signer.signature_mode === 'presential') {
                if (!this.signer.signature_host?.name || !this.signer.signature_host?.email) {
                    this.toast.error('Informe nome e e-mail do anfitrião para assinatura presencial.');
                    return;
                }
            }

            this.alterConfigSignerDoc({data:this.signer});
        },
    }
}
</script>

<style scoped>
.edit-signer {
    background: #f8fafc;
}
.edit-signer-card {
    background: #fff;
    border: 1px solid #d8e2ef;
    border-radius: 10px;
    margin-bottom: 14px;
    padding: 16px;
}
.edit-signer-card:last-child {
    margin-bottom: 0;
}
.edit-signer-header {
    align-items: flex-start;
    display: flex;
    gap: 12px;
    justify-content: space-between;
    margin-bottom: 14px;
}
.edit-signer-name {
    color: #0f172a;
    font-size: 16px;
    font-weight: 700;
}
.edit-signer-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    justify-content: flex-end;
}
.edit-signer-title {
    color: #0e5e4e;
    font-weight: 700;
    margin-bottom: 12px;
}
.edit-signer-mode {
    align-items: flex-start;
    border: 1px solid #d8e2ef;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    gap: 10px;
    height: 100%;
    margin-bottom: 0;
    padding: 12px;
}
.edit-signer-mode.is-selected {
    background: #f0f7ff;
    border-color: #0a3a75;
}
.edit-signer-mode.is-disabled {
    cursor: not-allowed;
    opacity: .7;
}
.edit-signer-mode input {
    margin-top: 4px;
}
@media (max-width: 767px) {
    .edit-signer-mode {
        margin-bottom: 10px;
    }
}
</style>
